/**
 * Flight input screen - add flight and calculate dose
 */

import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { FlightInputForm, DoseCard } from '../components';
import { useFlightStore, useDoseStore } from '../store';
import { estimateFlightDose } from '../services';
import type { DoseLog } from '../types';

export function FlightInputScreen() {
  const navigation = useNavigation();
  const { currentFlight, reset } = useFlightStore();
  const addLog = useDoseStore((s) => s.addLog);

  const handleCalculate = () => {
    const input = currentFlight as {
      origin: string;
      destination: string;
      date: string;
      durationMinutes: number;
      altitudeFeet?: number;
    };
    if (
      input.origin &&
      input.destination &&
      input.date &&
      input.durationMinutes > 0
    ) {
      const doseMicroSv = estimateFlightDose(input);
      const log: DoseLog = {
        id: `flight-${Date.now()}`,
        effectiveDoseMicroSv: doseMicroSv,
        source: 'flight',
        createdAt: new Date().toISOString(),
        flightId: undefined,
      };
      addLog(log);
      reset();
      navigation.goBack();
    }
  };

  const isValid =
    currentFlight.origin &&
    currentFlight.destination &&
    currentFlight.date &&
    (currentFlight.durationMinutes ?? 0) > 0;

  const estimatedDose = isValid
    ? estimateFlightDose(currentFlight as Parameters<typeof estimateFlightDose>[0])
    : 0;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Add Flight</Text>
      <FlightInputForm />
      {isValid && (
        <DoseCard doseMicroSv={estimatedDose} label="Estimated dose" />
      )}
      <Pressable
        style={[styles.button, !isValid && styles.buttonDisabled]}
        onPress={handleCalculate}
        disabled={!isValid}
      >
        <Text style={styles.buttonText}>Calculate & Log Dose</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, gap: 16 },
  title: { fontSize: 24, fontWeight: '700', marginBottom: 8 },
  button: {
    backgroundColor: '#007AFF',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  buttonDisabled: { opacity: 0.5 },
  buttonText: { color: '#fff', fontWeight: '600' },
});
